import React, { useState } from 'react'
import { Bill } from '../../../types'
import Item from './Item'
import { createStyles, makeStyles, ClickAwayListener } from '@material-ui/core'
import { Typography, Icon } from 'foundations-library/components'
import Tooltip from '@material-ui/core/Tooltip'

const useStyles = makeStyles(() =>
    createStyles({
        taxesAndFeesHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            '& svg': {
                verticalAlign: 'text-bottom'
            }
        },
        tooltip: {
            maxWidth: '250px',
            background: '#364047',
            padding: '14px 16px'
        },
        totalTaxes: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer'
        },
        taxesContainer: {
            transition: 'max-height .5s',
            maxHeight: ({ openTaxContainer }) => openTaxContainer ? '1000px' : 0,
            overflow: 'hidden'
        }
    })
)

interface TaxesAndFeesProps {
    bill: Bill
}

/**
 * Listing of taxes and fees, as part of the OrderSummary
 */
const TaxesAndFees = ({ bill } : TaxesAndFeesProps) : JSX.Element | null => {
    const [tooltipOpen, setTooltipOpen] = useState<boolean>(false)
    const [mouseOver, setMouseOver] = useState<boolean>(false)
    const [openTaxContainer, setOpenTaxContainer] = useState<boolean>(false)

    const classes = useStyles({ openTaxContainer })
    const section = bill.details.find(s => s.section === 'fees_and_taxes')

    const taxCharges = section?.charges?.reduce((total, value) => {
        return Number(total) + Number(value.cost)
    }, 0)

    // Round to 2 decimal places to avoid floating-point precision errors (e.g., 5.010000000000001 -> 5.01)
    const roundedTaxCharges = taxCharges ? Math.round(taxCharges * 100) / 100 : undefined

    return (
        <>
            <div className={classes.taxesAndFeesHeader} onClick={() => setTooltipOpen(!tooltipOpen)}>
                <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
                    <Tooltip
                        open={mouseOver || tooltipOpen}
                        onClose={() => setTooltipOpen(false)}
                        classes={{ tooltip: classes.tooltip }}
                        disableHoverListener={tooltipOpen}
                        interactive
                        title={
                            <>
                                <Typography remoteConfigID="signup_order_summary_taxes_tooltip" variant="helperText" color='#fff' />
                                <a target="_blank" href="https://support.phone.com/what-taxes-and-fees-are-billed-to-my-account" rel="noreferrer">
                                    Learn more
                                </a>
                            </>
                        }
                        placement='top'>
                        <Typography variant='subtitle3' align="left" color='neutral-900' style={{ whiteSpace: 'nowrap' }}>
                            Taxes & fees
                            &nbsp;
                            <Icon name='infotip-sm' color='neutral-300' size='small' onMouseOver={() => setMouseOver(true)} onMouseLeave={() => setMouseOver(false)} />
                        </Typography>
                    </Tooltip>
                </ClickAwayListener>
                {!section && (
                    <Typography
                        remoteConfigID="signup_order_summary_taxes_shown_later"
                        className={classes.helperText}
                        variant="body4"
                        color='neutral-500'
                        align="right"
                    />
                )}
                {
                    section && roundedTaxCharges !== undefined &&
                    <div className={classes.totalTaxes} onClick={() => setOpenTaxContainer(val => !val)}>
                        <Typography variant='captionLight'>{roundedTaxCharges}</Typography>
                        <Icon name={openTaxContainer ? 'down-chevron' : 'up-chevron'} size='medium' color='neutral-900' />
                    </div>
                }
            </div>

            <div className={classes.taxesContainer}>
                {section?.charges.map((charge, idx) => (
                    <Item key={idx} label={charge.name} cost={charge.cost} />
                ))}
            </div>
        </>
    )
}

export default TaxesAndFees
